/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts JS
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import MoserJS from 'bi/moser/moser.min'

const CACHE_LIMIT = 3
const { JoinGraph } = MoserJS

class JoinGraphService {

	/**
	 * The default constructor that gets initialized automatically by the glass service.
	 */
	constructor() {
		this.joinGraphs = []
	}

	/**
	 * Initialize gets called by the glass component once the service has been loaded.
	 * @param {object} glassContext - The glassContext provided by the glass service.
	 */
	initialize(glassContext) {
		this.glassContext = glassContext
		this.logger = this.glassContext.getCoreSvc('.Logger')
		return Promise.resolve()
	}

	/**
	 * Gets the related columns ids from the active joinGraph
	 * @param {array} columnIds - A list of column ids to query against. Example: ['HollywoodMovies_csv.Domestic_Gross']
	 * @returns {promise} Returns a promise that returns the related column ids once the joinGraph is loaded.
	 */
	getRelated(moduleId, columnIds) {
		return this.loadJoinGraph(moduleId)
			.then(({ joinGraph }) => {
				return joinGraph.getRelated(columnIds)
			})
			.catch(() => {
				return []
			})
	}

	/**
	 * Adds a joinGraph to the local cache
	 * @param {string} moduleId - A moduleId for a specified data source. Example: "i6F6FA19FBA034FEB828D5817074B4E97"
	 * @returns {promise} Returns a promise that finishes once the joinGraph is loaded.
	 */
	loadJoinGraph(moduleId) {
		// If the JoinGraph is already cached, use the local version.
		const cachedJoinGraph = this._findJoinGraph(moduleId)
		if (cachedJoinGraph) {
			return Promise.resolve(cachedJoinGraph)
		}

		// If the JoinGraph is not cached, fetch it from the server.
		return this._request(`v1/metadata/modules/${moduleId}/joingraph`)
			.then(response => {
				if (this.joinGraphs.length === CACHE_LIMIT) {
					this.joinGraphs.shift()
				}

				const newJoinGraph = { moduleId, joinGraph: new JoinGraph(response.data) }
				this.joinGraphs.push(newJoinGraph)

				return newJoinGraph
			})
			.catch(error => {
				this.logger.error('Unable to GET joinGraph. Verify that the moduleId is correct.', error)
			})
	}

	/**
	 * Clears the moduleId and joinGraph from the cache.
	 */
	clearCache() {
		this.joinGraphs = []
	}

	/**
	 * Searches the list of existing joinGraphs and returns one (if any) that matches the provided moduleId.
	 * @returns {object} Returns an object containing the moduleId and joinGraph
	 */
	_findJoinGraph(moduleId) {
		return this.joinGraphs.find(graph => graph.moduleId === moduleId)
	}

	/**
	 * Creates a GET request with the proper parameters to the Cognos Server.
	 * @param {string} url - The URL that we will be sending the GET request.
	 * @returns {promise} Returns a promise that finishes once the server has responded.
	 */
	_request(url) {
		return this.glassContext.getCoreSvc('.Ajax').ajax({
			type: 'GET',
			url: url,
			contentType: 'application/json',
			xhrFields: {
				withCredentials: true
			}
		})
	}

}

module.exports = JoinGraphService
